import { ImageProps } from 'next/image';
import { HTMLAttributeAnchorTarget, MouseEventHandler, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { AgencyCard as AgencyCardDesignSystem } from '../../../design-system/components/card/agency/AgencyCard';
import { ReviewRatingProps } from '../../../design-system/components/review/rating/ReviewRating';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { Agency } from '../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { PickDomain } from '../../utilities/types/PickDomain';
import { ReviewRatingFallback } from '../ReviewRating/ReviewRatingFallback';

export type Props = {
    agency: PickDomain<
        Agency,
        | 'id'
        | 'name'
        | 'slug'
        | 'nameTranslationRule'
        | 'tripReviewsAverageRating'
        | 'description'
        | 'picture'
        | 'betterTripIndex'
        | 'isLocalHero',
        {
            destination: PickDomain<Destination, 'slug', { name?: Destination['name'] }>;
        }
    >;
    image: Partial<ImageProps>;
    smallScreenDisplayHeader?: boolean;
    onClick?: MouseEventHandler<HTMLElement>;
    target?: HTMLAttributeAnchorTarget;
    showDestinationName?: boolean;
    trackingInformation: {
        click: TrackingProperties<typeof ampli.agencyClicked>;
    };
    reviewRatingVariant?: 'default' | 'single-star';
    showReview?: boolean;
    showDescription?: boolean;
    lightText?: boolean;
    name?: string;
};

export function AgencyCard({
    agency,
    image,
    smallScreenDisplayHeader,
    target,
    onClick,
    trackingInformation,
    showDestinationName = false,
    reviewRatingVariant,
    showReview = true,
    showDescription = true,
    lightText = false,
    name,
}: Props) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const agencyName =
        name ||
        formatMessage(
            { id: 'global.component.agencyCard.name' },
            {
                name: agency.name,
                nameTranslationRule: agency.nameTranslationRule,
            },
        );

    let reviewRatingProps: ReviewRatingProps | undefined;
    if (agency.tripReviewsAverageRating !== null) {
        const ariaLabel: ReviewRatingProps['ariaLabel'] = (rating) => {
            return formatMessage(
                { id: 'global.component.reviewRating.iconsDescription' },
                {
                    rating: rating.value,
                    maxRating: rating.max,
                },
            );
        };

        reviewRatingProps = {
            ratingValue: agency.tripReviewsAverageRating.rating.value,
            maxRating: agency.tripReviewsAverageRating.rating.max,
            minRating: agency.tripReviewsAverageRating.rating.min,
            ariaLabel,
            reviewsCount: formatMessage(
                { id: 'global.component.reviewRating.reviewsCount' },
                { reviewsCount: agency.tripReviewsAverageRating.count },
            ),
            fallback: (error) => <ReviewRatingFallback error={error} />,
        };
    }

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
        (event) => {
            if (typeof onClick === 'function') {
                onClick(event);
            }

            if (trackingInformation?.click) {
                trackEvent((ampli, defaultProperties) => {
                    ampli.agencyClicked({
                        ...defaultProperties,
                        ...trackingInformation.click,
                    });
                });
            }
        },
        [onClick, trackEvent, trackingInformation?.click],
    );

    return (
        <AgencyCardDesignSystem
            description={agency.description}
            image={{
                alt: agency.picture.legend ?? '',
                loader: imageLoaderForId({
                    aspectRatio: AgencyCardDesignSystem.PICTURE_RATIO,
                }),
                sizes: '300px',
                src: agency.picture.id.toString(),
                priority: false,
                ...image,
            }}
            name={agencyName}
            onClick={handleClick}
            reviewRatingProps={reviewRatingProps}
            smallScreenDisplayHeader={smallScreenDisplayHeader}
            target={target}
            url={generatePath('agency', {
                agencyId: agency.id,
                agencySlug: agency.slug,
                destinationSlug: agency.destination.slug,
            })}
            isLocalHero={agency.isLocalHero}
            destinationName={
                showDestinationName && agency.destination.name ? agency.destination.name : undefined
            }
            reviewRatingVariant={reviewRatingVariant}
            showReview={showReview}
            showDescription={showDescription}
            lightText={lightText}
        />
    );
}
